import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useAssetCardStoreV2 } from '@/stores/v2/AssetCardStore.ts';
import { useAuthStore } from '@/stores/AuthStore.ts';
import { useFrameworkStore } from '@/stores/FrameworkStore.ts';
import { useNotificationStore } from '@/stores/v2/NotificationStore.ts';
import { useOrganizationRisks } from '@/stores/v2/OrganizationRisks.ts';
import { useProjectRisks } from '@/stores/v2/ProjectRisks.ts';
import { useProjectsStoreV2 } from '@/stores/v2/ProjectStore.ts';
import { useRequirementStorev2 } from '@/stores/v2/RequirementStore.ts';
import { useAgentTestStore } from '@/stores/v2/AgentTestStore.ts';
import { useTestingStore } from '@/stores/v2/TestingStore.ts';
import { useJobsStore } from '@/stores/v2/JobsStore.ts';
import { useVendorStore } from '@/stores/VendorStore.ts';
import { usePoliciesStore } from '@/stores/v2/PoliciesStore.ts';
import { useTemplatesStore } from '@/stores/v2/TemplatesStore.ts';

export function useHeader() {
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const assetsStore = useAssetCardStoreV2();
  const projectsStoreV2 = useProjectsStoreV2();
  const requirementStore = useRequirementStorev2();
  const projectRisksStore = useProjectRisks();
  const testingStore = useTestingStore();
  const agentTestStore = useAgentTestStore();
  const jobsStore = useJobsStore();
  const notificationStore = useNotificationStore();
  const organizationsRisksStore = useOrganizationRisks();
  const vendorStore = useVendorStore();
  const frameworkStore = useFrameworkStore();
  const policiesStore = usePoliciesStore();
  const templatesStore = useTemplatesStore();

  const showUnread = ref(false);
  const unreadCount = computed(() => notificationStore.newNotificationCount);

  const projectDashboardTitle = computed(() => {
    if (projectsStoreV2.project) {
      return projectsStoreV2.project.name;
    }
    return '';
  });

  const requirementPageTitle = computed(() => {
    if (requirementStore.currentRequirement) {
      return requirementStore.currentRequirement.name;
    }
    return '';
  });

  const requirementUniqueCode = computed(() => {
    if (requirementStore.currentRequirement) {
      return requirementStore.currentRequirement.code;
    }
    return '';
  });

  const projectRiskPageTitle = computed(() => {
    if (projectRisksStore.currentRisk) {
      return projectRisksStore.currentRisk.name;
    }
    return '';
  });

  const projectRiskUniqueCode = computed(() => {
    if (projectRisksStore.currentRisk) {
      return projectRisksStore.currentRisk.unique_code;
    }
    return '';
  });

  const projectThreatPageTitle = computed(() => {
    if (projectRisksStore.currentThreat) {
      return projectRisksStore.currentThreat.name;
    }
    return '';
  });

  const projectThreatUniqueCode = computed(() => {
    if (projectRisksStore.currentThreat) {
      return projectRisksStore.currentThreat.unique_code;
    }
    return '';
  });

  const assetUniqueCode = computed(() => {
    if (assetsStore.currentAsset) {
      return assetsStore.currentAsset.unique_code;
    }
    return '';
  });

  const assetPageTitle = computed(() => {
    if (assetsStore.currentAsset) {
      return assetsStore.currentAsset.name;
    }
    return '';
  });

  const testingPageTitle = computed(() => {
    if (testingStore.currentTestingTest) {
      return testingStore.currentTestingTest.name;
    }
    return '';
  });

  const jobPageTitle = computed(() => jobsStore.currentJob?.name ?? '');

  const testingUniqueCode = computed(() => {
    if (testingStore.currentTestingTest) {
      return testingStore.currentTestingTest.unique_code;
    }
    return '';
  });

  const agentTestPageTitle = computed(() => agentTestStore.currentAgentTest?.name ?? '');

  const agentTestUniqueCode = computed(() => agentTestStore.currentAgentTest?.unique_code ?? '');

  const threatUniqueCode = computed(() => {
    if (organizationsRisksStore.currentThreat) {
      return organizationsRisksStore.currentThreat.unique_code;
    }
    return '';
  });

  const threatPageTitle = computed(() => {
    if (organizationsRisksStore.currentThreat) {
      return organizationsRisksStore.currentThreat.name;
    }
    return '';
  });

  const categoryUniqueCode = computed(() => {
    if (organizationsRisksStore.currentCategory) {
      return organizationsRisksStore.currentCategory.unique_code;
    }
    return '';
  });

  const categoryPageTitle = computed(() => {
    if (organizationsRisksStore.currentCategory) {
      return organizationsRisksStore.currentCategory.name;
    }
    return '';
  });

  const organizationRiskTitle = computed(() => {
    if (organizationsRisksStore.currentRisk) {
      return organizationsRisksStore.currentRisk.name;
    }
    return '';
  });

  const organizationRiskUniqueCode = computed(() => {
    if (organizationsRisksStore.currentRisk) {
      return organizationsRisksStore.currentRisk.unique_code;
    }
    return '';
  });

  const vendorPageTitle = computed(() => {
    if (vendorStore.currentVendor) {
      return vendorStore.currentVendor.name;
    }
    return '';
  });

  const frameworkTitle = computed(() => {
    if (frameworkStore.frameworkData) {
      return frameworkStore.frameworkData.name;
    }
    return '';
  });

  const templatePageTitle = computed(() => {
    if (templatesStore.currentTemplate) {
      return templatesStore.currentTemplate.name;
    }
    return '';
  });

  const policyPageTitle = computed(() => {
    if (policiesStore.currentPolicy) {
      return policiesStore.currentPolicy.name;
    }
    return '';
  });

  const policyUniqueCode = computed(() => {
    if (policiesStore.currentPolicy) {
      return policiesStore.currentPolicy.unique_code;
    }
    return '';
  });

  const pageTitle = computed(() => {
    switch (route.name) {
      // Project-related pages
      case 'ai-registry':
        return t('AI Registry');
      case 'edit-ai-system':
        return projectDashboardTitle.value;
      case 'projects':
        return t('AI Systems');
      case 'my-submissions':
        return t('My Submissions');
      case 'project-dashboard':
        return projectDashboardTitle.value;
      case 'project-overview':
        return projectDashboardTitle.value;
      case 'project-frameworks':
        return t('AI System Frameworks');
      case 'project-requirements':
        return t('AI System Requirements');
      case 'requirement-detail':
        return requirementPageTitle.value;
      case 'project-controls':
        return t('AI System Controls');
      case 'project-graph':
        return t('AI System Graph');
      case 'project-settings':
        return t('Settings');
      case 'project-risks':
        return t('Risks');
      case 'risk-detail':
        return projectRiskPageTitle.value;
      case 'risk-quantification':
        return t('Risk Quantification');
      case 'threat-quantification-detail':
        return projectThreatPageTitle.value;
      case 'project-evidence':
        return t('Evidence Manager');
      case 'project-assets':
        return t('Assets');
      case 'asset-detail':
        return assetPageTitle.value;
      case 'project-runtime-inspection':
        return t('Runtime Inspection');
      case 'test-detail':
        return testingPageTitle.value;
      case 'agent-test-detail':
        return agentTestPageTitle.value;
      case 'project-jobs':
        return t('Jobs');
      case 'job-detail':
        return jobPageTitle.value;

      // Organization-related pages
      case 'organization-risk-management':
        return t('Risk Management');
      case 'organization-users':
        return t('User Management');
      case 'organization-frameworks':
        return t('Frameworks');
      case 'organization-framework-detail':
        return frameworkTitle.value;
      case 'organization-settings':
        return t('Settings');
      case 'organization-usage':
        return t('Usage');
      case 'organization-taxonomy-threat-detail':
        return threatPageTitle.value;
      case 'organization-taxonomy-category-detail':
        return categoryPageTitle.value;
      case 'organization-taxonomy-risk-detail':
        return organizationRiskTitle.value;
      case 'organization-source-connectors':
        return t('Source & Connectors');
      case 'organization-notifications':
        return t('Notifications Center');

      // Admin-related pages
      case 'admin-ai-usage':
        return t('AI Usage');
      case 'admin-ai-usage-events':
        return t('AI usage events');
      case 'admin-ai-pricing':
        return t('AI Pricing');
      case 'admin-ai-usage-organization-detail':
        return t('Organization usage');
      case 'admin-organizations':
        return t('Organizations');
      case 'admin-new-template':
        return t('New Template');
      case 'admin-templates':
      case 'admin-templates-overview':
        return t('Policy Templates');
      case 'admin-template-detail':
      case 'admin-template-version-detail':
        return templatePageTitle.value;
      case 'admin-frameworks':
      case 'admin-frameworks-overview':
        return t('Frameworks');
      case 'admin-framework-detail':
        return frameworkTitle.value;

      // Settings-related pages
      case 'settings':
      case 'settings-default':
      case 'user-settings':
        return t('User Settings');
      case 'settings-notifications':
        return t('Notification Preferences');

      // Vendor-related pages
      case 'vendors':
      case 'vendors-overview':
        return t('Vendors');
      case 'vendor-detail':
        return vendorPageTitle.value;

      case 'policies':
      case 'policies-default':
      case 'policies-my-policies':
        return t('My Tasks');
      case 'policies-management':
        return t('Policy Management');
      case 'policies-templates-catalog':
        return t('Templates Catalog');
      case 'policies-acknowledgments':
        return t('Acknowledgements');
      case 'my-policy-version-detail':
      case 'management-policy-detail':
      case 'management-policy-version-detail':
        return policyPageTitle.value;

      // Notifications page
      case 'notifications':
        return t('Notifications');

      // Default case
      default:
        return '';
    }
  });

  const pageUniqueCode = computed(() => {
    switch (route.name) {
      // Project-related pages
      case 'requirement-detail':
        return requirementUniqueCode.value;
      case 'asset-detail':
        return assetUniqueCode.value;
      case 'risk-detail':
        return projectRiskUniqueCode.value;
      case 'test-detail':
        return testingUniqueCode.value;
      case 'agent-test-detail':
        return agentTestUniqueCode.value;
      case 'threat-quantification-detail':
        return projectThreatUniqueCode.value;

      // Organization-related pages
      case 'organization-taxonomy-threat-detail':
        return threatUniqueCode.value;
      case 'organization-taxonomy-category-detail':
        return categoryUniqueCode.value;
      case 'organization-taxonomy-risk-detail':
        return organizationRiskUniqueCode.value;

      case 'my-policy-version-detail':
      case 'management-policy-detail':
      case 'management-policy-version-detail':
        return policyUniqueCode.value;

      // Default case
      default:
        return '';
    }
  });

  const frameworkData = computed(() => {
    switch (route.name) {
      case 'organization-framework-detail':
      case 'admin-framework-detail':
        return frameworkStore.frameworkData;
      default:
        return null;
    }
  });

  const showBackArrow = computed(() => {
    return route.name?.toString().includes('-detail') && !!window.history.state?.back;
  });

  function goBack() {
    router.back();
  }

  watch(
    () => notificationStore.newNotificationCount,
    (newV) => {
      showUnread.value = newV > 0;
    },
    { immediate: true },
  );

  return {
    t,
    route,
    authStore,
    showUnread,
    unreadCount,
    showBackArrow,
    goBack,
    pageTitle,
    pageUniqueCode,
    frameworkData,
  };
}
